import React, { useState, useEffect } from 'react';
import { X, Calendar, ChevronDown, Plus, Upload, Trash2, Eye, Download, Loader2, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import axios from 'axios';
// v2: Fixed asset loading to use correct API endpoint

// Import components
import ItemsList from '../../../review/ItemsList';
import AssetsList from '../../../review/AssetsList';
import AssetPreview from '../../../review/AssetPreview';
import ContentEditorPanel from './ContentEditorPanel';
import NewContentItemModal from './NewContentItemModal';
import AssetCreationWizard from '../../../assets/creation/AssetCreationWizard';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://content-review-api.ryantoth2410.workers.dev';

interface Plan {
  id: number;
  type: 'weekly' | 'monthly';
  name: string;
  start_date: string;
  end_date: string;
  status: string;
  client_id?: number;
}

interface ContentItem {
  id: number;
  type: string;
  title: string;
  description?: string;
  content?: string;
  status: string;
  created_at: string;
  updated_at?: string;
  assets?: Asset[];
}

interface Asset {
  id: number;
  content_id?: number;
  asset_type: string;
  file_name: string;
  file_size?: number;
  mime_type?: string;
  r2_key: string;
  created_at: string;
  asset_approvals?: any[];
}

interface PlanDetailModalRedesignedProps {
  isOpen: boolean;
  plan: Plan | null;
  onClose: () => void;
}

export default function PlanDetailModalRedesigned({ isOpen, plan, onClose }: PlanDetailModalRedesignedProps) {
  const [loading, setLoading] = useState(false);
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<ContentItem | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showNewItemModal, setShowNewItemModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [feedbackPins, setFeedbackPins] = useState<any[]>([]);
  const [isPlacingPin, setIsPlacingPin] = useState(false);
  const [tempPin, setTempPin] = useState<{ x: number; y: number } | null>(null);
  const [showAssetWizard, setShowAssetWizard] = useState(false);

  // Helper function to get both AM and Client statuses
  const getAssetStatuses = (asset: Asset) => {
    const amApproval = asset.asset_approvals?.find((a: any) => a.approver_type === 'account_manager');
    const clientApproval = asset.asset_approvals?.find((a: any) => a.approver_type === 'client');

    return {
      am: amApproval?.status || 'pending',
      client: clientApproval?.status || 'pending',
      // Overall status for background color
      overall: getOverallStatus(amApproval?.status, clientApproval?.status)
    };
  };

  // Helper function to determine overall status
  const getOverallStatus = (amStatus?: string, clientStatus?: string) => {
    // Both approved
    if (amStatus === 'approved' && clientStatus === 'approved') {
      return 'approved';
    }
    // Either rejected
    if (amStatus === 'rejected' || clientStatus === 'rejected') {
      return 'rejected';
    }
    // Either needs edit
    if (amStatus === 'needs_edit' || clientStatus === 'needs_edit') {
      return 'needs_edit';
    }
    // At least one in review
    if (amStatus === 'in_review' || clientStatus === 'in_review' ||
        amStatus === 'approved' || clientStatus === 'approved') {
      return 'in_review';
    }
    return 'pending';
  };

  // Helper function to get status icon
  const getStatusIcon = (status: string, size: 'small' | 'normal' = 'normal') => {
    const iconClass = size === 'small' ? "w-3 h-3" : "w-3.5 h-3.5";
    switch (status) {
      case 'approved':
        return <CheckCircle className={`${iconClass} text-green-500`} />;
      case 'rejected':
        return <XCircle className={`${iconClass} text-red-500`} />;
      case 'needs_edit':
        return <AlertCircle className={`${iconClass} text-yellow-500`} />;
      case 'in_review':
        return <Clock className={`${iconClass} text-blue-500`} />;
      default:
        return <Clock className={`${iconClass} text-gray-400`} />;
    }
  };

  // Helper component to show dual status
  const DualStatusDisplay = ({ statuses }: { statuses: { am: string; client: string; overall: string } }) => (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1">
        <span className="text-xs text-gray-600 font-medium">AM:</span>
        {getStatusIcon(statuses.am, 'small')}
      </div>
      <div className="flex items-center gap-1">
        <span className="text-xs text-gray-600 font-medium">CL:</span>
        {getStatusIcon(statuses.client, 'small')}
      </div>
    </div>
  );

  // Helper function to get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'text-green-600 bg-green-50';
      case 'rejected':
        return 'text-red-600 bg-red-50';
      case 'needs_edit':
        return 'text-yellow-600 bg-yellow-50';
      case 'in_review':
        return 'text-blue-600 bg-blue-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  // Calculate asset progress for both AM and Client
  const getAssetProgress = () => {
    if (!selectedItem?.assets || selectedItem.assets.length === 0) {
      return {
        total: 0,
        amApproved: 0,
        clientApproved: 0,
        bothApproved: 0,
        rejected: 0,
        pending: 0,
        percentage: 0
      };
    }

    const stats = {
      total: selectedItem.assets.length,
      amApproved: 0,
      clientApproved: 0,
      bothApproved: 0,
      amRejected: 0,
      clientRejected: 0,
      anyRejected: 0,
      pending: 0
    };

    selectedItem.assets.forEach((asset) => {
      const statuses = getAssetStatuses(asset);

      // Count AM approvals
      if (statuses.am === 'approved') stats.amApproved++;
      if (statuses.am === 'rejected') stats.amRejected++;

      // Count Client approvals
      if (statuses.client === 'approved') stats.clientApproved++;
      if (statuses.client === 'rejected') stats.clientRejected++;

      // Count both approved
      if (statuses.am === 'approved' && statuses.client === 'approved') {
        stats.bothApproved++;
      }

      // Count any rejected
      if (statuses.am === 'rejected' || statuses.client === 'rejected') {
        stats.anyRejected++;
      }

      // Count pending (neither has acted)
      if (statuses.am === 'pending' && statuses.client === 'pending') {
        stats.pending++;
      }
    });

    const percentage = stats.total > 0 ? Math.round((stats.bothApproved / stats.total) * 100) : 0;

    return { ...stats, percentage };
  };

  useEffect(() => {
    console.log('Plan Detail Modal - isOpen:', isOpen, 'plan:', plan);
    if (isOpen && plan) {
      loadPlanContent();
    }
  }, [isOpen, plan]);

  // Load assets when selected item changes
  useEffect(() => {
    if (selectedItem && selectedItem.id) {
      loadItemAssets(selectedItem.id);
    }
  }, [selectedItem?.id]); // Only trigger on ID change, not object reference change

  const loadPlanContent = async () => {
    if (!plan || !plan.id) {
      console.log('No plan or plan ID available');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      // First get plan with items
      const response = await axios.get(
        `${API_BASE_URL}/plans/${plan.id}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.items) {
        // For each item, load its assets
        const itemsWithAssets = await Promise.all(
          response.data.items.map(async (item: ContentItem) => {
            try {
              const assetsResponse = await axios.get(
                `${API_BASE_URL}/assets?content_id=${item.id}`,
                {
                  headers: { 'Authorization': `Bearer ${token}` }
                }
              );
              return { ...item, assets: assetsResponse.data || [] };
            } catch (error) {
              console.error(`Failed to load assets for item ${item.id}:`, error);
              return { ...item, assets: [] };
            }
          })
        );

        setContentItems(itemsWithAssets);
        if (itemsWithAssets.length > 0 && !selectedItem) {
          setSelectedItem(itemsWithAssets[0]);
        }
      }
    } catch (error: any) {
      console.error('Failed to load plan content:', error);
      console.error('Plan ID:', plan?.id);
      console.error('Error details:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadItemAssets = async (itemId: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/assets?content_id=${itemId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      // Update the selected item with fresh assets
      if (selectedItem && selectedItem.id === itemId) {
        setSelectedItem(prev => prev ? { ...prev, assets: response.data || [] } : null);

        // Also update in contentItems array
        setContentItems(prev => prev.map(item =>
          item.id === itemId ? { ...item, assets: response.data || [] } : item
        ));
      }
    } catch (error) {
      console.error('Failed to load assets:', error);
    }
  };

  const handleCreateNewItem = async (formData: any) => {
    if (!plan) return;

    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${API_BASE_URL}/content`,
      {
        ...formData,
        plan_id: plan.id,
        client_id: plan.client_id
      },
      {
        headers: { 'Authorization': `Bearer ${token}` }
      }
    );

    // Reload content
    await loadPlanContent();

    // Select the new item with empty assets array initially
    if (response.data) {
      const newItem = { ...response.data, assets: [] };
      setSelectedItem(newItem);

      // Add to content items if not already there
      setContentItems(prev => {
        const exists = prev.some(item => item.id === newItem.id);
        if (!exists) {
          return [...prev, newItem];
        }
        return prev;
      });
    }
  };

  const handleSaveContent = async (updatedContent: Partial<ContentItem>) => {
    if (!selectedItem) return;

    const token = localStorage.getItem('token');
    await axios.put(
      `${API_BASE_URL}/content/${selectedItem.id}`,
      updatedContent,
      {
        headers: { 'Authorization': `Bearer ${token}` }
      }
    );

    // Reload content
    await loadPlanContent();
    setIsEditing(false);
    alert('Content updated successfully!');
  };

  const handleSaveAsset = async (assetId: number, content: string) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/assets/${assetId}`,
        { content },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      // Reload assets for the current item
      if (selectedItem) {
        await loadItemAssets(selectedItem.id);
      }
      alert('Asset updated successfully!');
    } catch (error) {
      console.error('Failed to save asset:', error);
      throw error; // Re-throw so the component can handle it
    }
  };

  const handleFileUpload = async () => {
    if (!uploadFile || !selectedItem) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('content_id', selectedItem.id.toString());
    formData.append('asset_type', getAssetTypeFromFile(uploadFile));

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/assets/upload`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Reload assets for the current item
      await loadItemAssets(selectedItem.id);
      setUploadFile(null);
      alert('Asset uploaded successfully!');
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload asset');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteAsset = async (assetId: number) => {
    if (!confirm('Are you sure you want to delete this asset?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/assets/${assetId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      // Reload assets for the current item
      if (selectedItem) {
        await loadItemAssets(selectedItem.id);
      }
      alert('Asset deleted successfully!');
    } catch (error) {
      console.error('Failed to delete asset:', error);
      alert('Failed to delete asset');
    }
  };

  const getAssetTypeFromFile = (file: File): string => {
    const mimeType = file.type;
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    if (mimeType === 'application/pdf') return 'pdf';
    if (mimeType.includes('json')) return 'meta';
    return 'document';
  };

  const handlePlacePin = (e: React.MouseEvent<HTMLDivElement>) => {
    // Placeholder for pin placement logic
    console.log('Pin placement at:', e.clientX, e.clientY);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40">
      <div className="bg-white rounded-lg w-[95vw] h-[90vh] flex flex-col overflow-hidden">
        {/* Simplified Header */}
        <div className="px-6 py-3 border-b border-gray-200 bg-white flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-900">{plan?.name}</h2>
            <span className={`px-2 py-1 text-xs rounded-full ${
              plan?.status === 'active' ? 'bg-green-100 text-green-800' :
              plan?.status === 'completed' ? 'bg-blue-100 text-blue-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {plan?.status}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Main Content */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        ) : (
          <div className="flex-1 flex overflow-hidden">
            {/* Left Sidebar - Narrower */}
            <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col">
              {/* Content Dropdown */}
              <div className="p-3 bg-white border-b border-gray-200">
                <div className="relative">
                  <button
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md flex items-center justify-between hover:bg-gray-50"
                  >
                    <span className="text-sm font-medium truncate">
                      {selectedItem ? selectedItem.title : 'Select content item'}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {showDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-64 overflow-y-auto">
                      {contentItems.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => {
                            setSelectedItem(item);
                            setShowDropdown(false);
                            setIsEditing(false);
                          }}
                          className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between ${
                            selectedItem?.id === item.id ? 'bg-blue-50' : ''
                          }`}
                        >
                          <span className="text-sm truncate">{item.title}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            item.status === 'approved' ? 'bg-green-100 text-green-800' :
                            item.status === 'published' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {item.status}
                          </span>
                        </button>
                      ))}
                      <button
                        onClick={() => {
                          setShowDropdown(false);
                          setShowNewItemModal(true);
                        }}
                        className="w-full px-3 py-2 text-left bg-blue-50 hover:bg-blue-100 border-t border-gray-200 flex items-center gap-2 text-blue-600"
                      >
                        <Plus className="w-4 h-4" />
                        <span className="text-sm font-medium">Add New Content</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Assets List - Compact */}
              <div className="flex-1 overflow-hidden flex flex-col">
                <div className="px-3 py-2 bg-white border-b border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-900">
                      Assets {selectedItem?.assets && `(${selectedItem.assets.length})`}
                    </h3>
                    <button
                      onClick={() => setShowAssetWizard(true)}
                      className="p-1.5 bg-white hover:bg-gray-50 rounded-md border border-gray-300 transition-colors duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!selectedItem}
                      title="Add new asset"
                    >
                      <Plus className="w-4 h-4 text-gray-700" />
                    </button>
                  </div>

                  {/* Progress Summary */}
                  {selectedItem?.assets && selectedItem.assets.length > 0 && (
                    <div className="space-y-2">
                      {/* Overall Progress Bar */}
                      <div>
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                          <span>Overall Progress</span>
                          <span>{getAssetProgress().bothApproved}/{getAssetProgress().total}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className="bg-green-500 h-1.5 rounded-full transition-all duration-200"
                            style={{ width: `${getAssetProgress().percentage}%` }}
                          />
                        </div>
                      </div>

                      {/* Dual Status Summary */}
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="bg-gray-50 rounded p-1">
                          <div className="font-medium text-gray-700">Account Manager</div>
                          <div className="flex items-center gap-1 mt-1">
                            <CheckCircle className="w-3 h-3 text-green-500" />
                            <span>{getAssetProgress().amApproved}/{getAssetProgress().total}</span>
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded p-1">
                          <div className="font-medium text-gray-700">Client</div>
                          <div className="flex items-center gap-1 mt-1">
                            <CheckCircle className="w-3 h-3 text-green-500" />
                            <span>{getAssetProgress().clientApproved}/{getAssetProgress().total}</span>
                          </div>
                        </div>
                      </div>

                      {/* Quick Stats */}
                      {(getAssetProgress().anyRejected > 0 || getAssetProgress().pending > 0) && (
                        <div className="flex items-center gap-2 text-xs pt-1 border-t border-gray-200">
                          {getAssetProgress().anyRejected > 0 && (
                            <span className="flex items-center gap-1">
                              <XCircle className="w-3 h-3 text-red-500" />
                              <span className="text-red-600">{getAssetProgress().anyRejected} rejected</span>
                            </span>
                          )}
                          {getAssetProgress().pending > 0 && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3 text-gray-400" />
                              <span className="text-gray-600">{getAssetProgress().pending} pending</span>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto p-2">
                  {selectedItem?.assets && selectedItem.assets.length > 0 ? (
                    <div className="space-y-1">
                      {selectedItem.assets.map((asset) => {
                        const statuses = getAssetStatuses(asset);
                        return (
                          <button
                            key={asset.id}
                            onClick={() => setSelectedAsset(asset)}
                            className={`group w-full px-2 py-2 text-left rounded-md transition-colors duration-200 text-sm ${
                              selectedAsset?.id === asset.id
                                ? 'bg-blue-100 border-blue-500'
                                : statuses.overall === 'approved'
                                  ? 'bg-green-50 hover:bg-green-100 border-green-200'
                                  : statuses.overall === 'rejected'
                                    ? 'bg-red-50 hover:bg-red-100 border-red-200'
                                    : statuses.overall === 'needs_edit'
                                      ? 'bg-yellow-50 hover:bg-yellow-100 border-yellow-200'
                                      : 'bg-white hover:bg-gray-50 border-gray-200'
                            } border`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="truncate text-xs font-medium">{asset.file_name}</span>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteAsset(asset.id);
                                }}
                                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded transition-opacity duration-200"
                              >
                                <Trash2 className="w-3 h-3 text-red-600" />
                              </button>
                            </div>
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-xs text-gray-500">{asset.asset_type}</span>
                              <DualStatusDisplay statuses={statuses} />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-4 text-center text-gray-500">
                      <p className="text-xs">No assets uploaded</p>
                      <button
                        onClick={() => document.querySelector<HTMLInputElement>('input[type="file"]')?.click()}
                        className="mt-2 text-blue-600 hover:text-blue-700 text-xs font-medium"
                        disabled={!selectedItem}
                      >
                        Upload asset
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Main Content Area - Asset Preview Takes Priority */}
            <div className="flex-1 flex flex-col bg-white relative">
              {selectedAsset ? (
                <>

                  {/* Asset Preview Content - Full Space */}
                  <div className="flex-1 overflow-auto">
                    <AssetPreview
                      selectedAsset={selectedAsset}
                      feedbackPins={feedbackPins}
                      isPlacingPin={isPlacingPin}
                      tempPin={tempPin}
                      onPlacePin={handlePlacePin}
                      apiBaseUrl={API_BASE_URL}
                      reviewType="account_manager"
                      onSaveAsset={handleSaveAsset}
                    />
                  </div>
                </>
              ) : (
                /* Content Editor Panel - When no asset is selected */
                <ContentEditorPanel
                  content={selectedItem}
                  isEditing={isEditing}
                  onEdit={() => setIsEditing(true)}
                  onSave={handleSaveContent}
                  onCancel={() => setIsEditing(false)}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* New Content Item Modal */}
      <NewContentItemModal
        isOpen={showNewItemModal}
        onClose={() => setShowNewItemModal(false)}
        onSubmit={handleCreateNewItem}
        planId={plan?.id || 0}
        clientId={plan?.client_id}
      />

      {/* Asset Creation Wizard */}
      <AssetCreationWizard
        isOpen={showAssetWizard && !!selectedItem}
        onClose={() => setShowAssetWizard(false)}
        contentId={selectedItem?.id || 0}
        onSuccess={() => {
          setShowAssetWizard(false);
          if (selectedItem) {
            loadItemAssets(selectedItem.id);
          }
        }}
        apiBaseUrl={API_BASE_URL}
        clientId={plan?.client_id}
      />
    </div>
  );
};